import { useEffect, useRef, useState } from "react";
import { apiFetch } from "./auth";
import { getConsent, visitorId } from "./track";

const KEY = "vl-copy";
const SLOTS = ["app_hero", "app_guarantees", "biz_hero", "biz_how", "biz_guarantees", "biz_pricing", "biz_final_cta", "biz_platform", "biz_guide_nudge", "biz_cases"];
let loading = null;
let cache = null;

const read = () => { try { return JSON.parse(sessionStorage.getItem(KEY)) || {}; } catch { return {}; } };
const assignmentsOf = (c) => Object.fromEntries(Object.entries(c || {}).map(([s, v]) => [s, v.variant_id]));

async function load() {
  if (cache) return cache;
  if (!loading) {
    loading = apiFetch(`/api/public/copy?slots=${SLOTS.join(",")}&visitor_id=${encodeURIComponent(visitorId())}`).then((r) => (r.ok ? r.json() : {})).then((d) => { cache = d || {}; sessionStorage.setItem(KEY, JSON.stringify(cache)); return cache; }).catch(() => (cache = read()));
  }
  return loading;
}

// ?copy=slot:variant_id forces a variant for in-place previews (no events are recorded).
const forced = () => { try { const v = new URLSearchParams(window.location.search).get("copy"); if (!v) return null; const i = v.indexOf(":"); return i > 0 ? { slot: v.slice(0, i), variant_id: v } : null; } catch { return null; } };
export const isPreview = () => !!forced();

export const copyEvent = (type, slots) => {
  if (isPreview() || !getConsent()?.analytics) return;
  const c = cache || read();
  const assignments = Object.fromEntries(Object.entries(assignmentsOf(c)).filter(([s]) => !slots || slots.includes(s)));
  if (!Object.keys(assignments).length) return;
  let persona = ""; try { persona = localStorage.getItem("vl-biz-persona") || ""; } catch {}
  apiFetch("/api/public/copy/event", { method: "POST", body: JSON.stringify({ type, visitor_id: visitorId(), path: window.location.pathname, persona, assignments }), keepalive: true }).catch(() => {});
};

// Records a lead against every copy variant this visitor saw (called after a demo / contact form succeeds).
export const copyLead = () => copyEvent("lead");

// Returns the assigned copy for a section plus a ref (view tracked once when 50% visible) and a click handler for its CTAs.
export function useCopy(slot, fallback) {
  const [copy, setCopy] = useState(() => (cache || read())[slot] || null);
  const ref = useRef(null);
  const seen = useRef(false);
  const f = forced();
  useEffect(() => {
    let on = true;
    if (f && f.slot === slot) {
      apiFetch(`/api/public/copy/variant/${encodeURIComponent(f.variant_id)}`).then((r) => (r.ok ? r.json() : null)).then((v) => { if (on && v) { setCopy(v); setTimeout(() => ref.current?.scrollIntoView({ block: "start" }), 300); } });
      return () => { on = false; };
    }
    load().then((c) => { if (on && c?.[slot]) setCopy(c[slot]); });
    return () => { on = false; };
  }, [slot]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const el = ref.current;
    if (!el || seen.current || !copy) return;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting) && !seen.current) { seen.current = true; copyEvent("view", [slot]); io.disconnect(); } }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [copy, slot]);
  const click = () => copyEvent("click", [slot]);
  return { title: copy?.title || fallback.title, desc: copy?.desc || fallback.desc, variant: copy?.variant_id || "control", ref, click };
}
